import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import {
  Plus,
  Search,
  TrendingDown,
  Calendar,
  DollarSign,
  Tag,
  Edit,
  Trash2,
  X,
  FileText,
  Receipt,
  CheckCircle,
  Upload,
  Paperclip,
  Image,
  Eye,
  Loader2,
  Printer,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// IRS Schedule C aligned expense categories
const expenseCategories = [
  { value: 'advertising', label: 'Advertising & Marketing', taxLine: 'Line 8' },
  { value: 'vehicle_expenses', label: 'Car & Vehicle Expenses', taxLine: 'Line 9' },
  { value: 'commissions_fees', label: 'Commissions & Fees', taxLine: 'Line 10' },
  { value: 'contract_labor', label: 'Contract Labor (1099)', taxLine: 'Line 11' },
  { value: 'depreciation', label: 'Depreciation & Section 179', taxLine: 'Line 13' },
  { value: 'employee_benefits', label: 'Employee Benefits', taxLine: 'Line 14' },
  { value: 'insurance', label: 'Insurance (non-health)', taxLine: 'Line 15' },
  { value: 'interest_mortgage', label: 'Interest - Mortgage', taxLine: 'Line 16a' },
  { value: 'interest_other', label: 'Interest - Other', taxLine: 'Line 16b' },
  { value: 'legal_professional', label: 'Legal & Professional Services', taxLine: 'Line 17' },
  { value: 'office_expense', label: 'Office Expense', taxLine: 'Line 18' },
  { value: 'pension_profit_sharing', label: 'Pension & Profit-Sharing', taxLine: 'Line 19' },
  { value: 'rent_vehicles', label: 'Rent - Vehicles/Equipment', taxLine: 'Line 20a' },
  { value: 'rent_other', label: 'Rent - Business Property', taxLine: 'Line 20b' },
  { value: 'repairs_maintenance', label: 'Repairs & Maintenance', taxLine: 'Line 21' },
  { value: 'supplies', label: 'Supplies', taxLine: 'Line 22' },
  { value: 'taxes_licenses', label: 'Taxes & Licenses', taxLine: 'Line 23' },
  { value: 'registration_fees', label: 'Business Registration Fees', taxLine: 'Line 23' },
  { value: 'state_annual_report', label: 'State Annual Report Fee', taxLine: 'Line 23' },
  { value: 'travel', label: 'Travel', taxLine: 'Line 24a' },
  { value: 'meals', label: 'Meals (50% deductible)', taxLine: 'Line 24b' },
  { value: 'utilities', label: 'Utilities', taxLine: 'Line 25' },
  { value: 'wages', label: 'Wages', taxLine: 'Line 26' },
  { value: 'software_subscriptions', label: 'Software & Subscriptions', taxLine: 'Line 27' },
  { value: 'hosting_domains', label: 'Hosting & Domains', taxLine: 'Line 27' },
  { value: 'bank_fees', label: 'Bank & Payment Fees', taxLine: 'Line 27' },
  { value: 'education_training', label: 'Education & Training', taxLine: 'Line 27' },
  { value: 'other_expenses', label: 'Other Expenses', taxLine: 'Line 27' },
];

const paymentMethods = [
  { value: 'business_card', label: 'Business Credit Card' },
  { value: 'business_checking', label: 'Business Checking' },
  { value: 'personal_card', label: 'Personal Card (Reimbursable)' },
  { value: 'cash', label: 'Cash' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'other', label: 'Other' },
];

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [monthFilter, setMonthFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [evidencePreview, setEvidencePreview] = useState(null);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [viewingEvidence, setViewingEvidence] = useState(null);

  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - i).toString());
  
  const monthOptions = [
    { value: 'all', label: 'All Months' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const [showReportModal, setShowReportModal] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const reportRef = useRef(null);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    category: 'office_expense',
    amount: '',
    payment_method: 'business_card',
    vendor: '',
    receipt_number: '',
    is_tax_deductible: true,
    notes: '',
    tax_year: null,
    evidence_file: null,
  });

  useEffect(() => {
    fetchExpenses();
    fetchCompanySettings();
  }, []);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`);
      setExpenses(response.data);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Expense_Report_${yearFilter}_${monthFilter !== 'all' ? monthFilter : 'Full_Year'}`,
  });

  const getReportPeriodLabel = () => {
    if (yearFilter === 'all') return 'All Time';
    const monthName = monthOptions.find(m => m.value === monthFilter)?.label || '';
    if (monthFilter === 'all') return `Year ${yearFilter}`;
    return `${monthName} ${yearFilter}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
      };
      if (selectedExpense) {
        await axios.put(`${API}/expenses/${selectedExpense.id}`, payload);
      } else {
        await axios.post(`${API}/expenses`, payload);
      }
      fetchExpenses();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save expense:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await axios.delete(`${API}/expenses/${id}`);
      fetchExpenses();
    } catch (error) {
      console.error('Failed to delete expense:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      category: 'office_expense',
      amount: '',
      payment_method: 'business_card',
      vendor: '',
      receipt_number: '',
      is_tax_deductible: true,
      notes: '',
      evidence_file: null,
    });
    setSelectedExpense(null);
    setEvidencePreview(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload an image (JPG, PNG, GIF, WebP) or PDF file');
      return;
    }

    const maxSize = file.type === 'application/pdf' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File too large. Max size: ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    setUploadingFile(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/expenses/upload-evidence`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData({ ...formData, evidence_file: response.data.filename });
      
      // Set preview for images
      if (file.type.startsWith('image/')) {
        setEvidencePreview(URL.createObjectURL(file));
      } else {
        setEvidencePreview('pdf');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  const removeEvidence = async () => {
    if (formData.evidence_file) {
      try {
        await axios.delete(`${API}/expenses/evidence/${formData.evidence_file}`);
      } catch (error) {
        console.error('Failed to delete evidence:', error);
      }
    }
    setFormData({ ...formData, evidence_file: null });
    setEvidencePreview(null);
  };

  const viewEvidence = (filename) => {
    setViewingEvidence(filename);
    setShowEvidenceModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredExpenses = expenses.filter(item => {
    const matchesSearch = 
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.vendor?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    
    // Year filter
    const itemDate = item.date || '';
    const itemYear = itemDate.substring(0, 4);
    const matchesYear = yearFilter === 'all' || itemYear === yearFilter;
    
    // Month filter
    const itemMonth = itemDate.substring(5, 7);
    const matchesMonth = monthFilter === 'all' || itemMonth === monthFilter;
    
    return matchesSearch && matchesCategory && matchesYear && matchesMonth;
  });

  const totalExpenses = filteredExpenses.reduce((sum, item) => sum + item.amount, 0);
  const taxDeductible = filteredExpenses
    .filter(e => e.is_tax_deductible)
    .reduce((sum, e) => sum + e.amount, 0);

  const expensesByCategory = expenseCategories.map(cat => ({
    ...cat,
    total: filteredExpenses.filter(e => e.category === cat.value).reduce((sum, e) => sum + e.amount, 0),
  })).filter(cat => cat.total > 0).sort((a, b) => b.total - a.total);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl text-white font-medium">Expenses</h1>
          <p className="text-gray-400 text-sm">Track business expenses for tax deductions (IRS Schedule C)</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowReportModal(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-4 py-2 rounded-xl transition-colors text-sm"
            data-testid="generate-expense-report-btn"
          >
            <FileText className="w-4 h-4" />
            <span className="hidden xs:inline">Generate </span>Report
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2 rounded-xl transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Expense
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-red-900/30 to-red-800/20 rounded-2xl border border-red-500/20 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-400" />
            </div>
            <span className="text-gray-400 text-sm">
              Total Expenses {yearFilter !== 'all' ? `(${yearFilter})` : '(All Time)'}
            </span>
          </div>
          <div className="text-3xl text-white font-bold">{formatCurrency(totalExpenses)}</div>
        </div>
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-green-400" />
            </div>
            <span className="text-gray-400 text-sm">Tax Deductible</span>
          </div>
          <div className="text-3xl text-white font-bold">{formatCurrency(taxDeductible)}</div>
        </div>
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-gray-400 text-sm">Transactions</span>
          </div>
          <div className="text-3xl text-white font-bold">{filteredExpenses.length}</div>
        </div>
      </div>

      {/* Category Breakdown for Taxes */}
      {expensesByCategory.length > 0 && (
        <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
          <h3 className="text-white font-medium mb-4">Expenses by IRS Category (Schedule C)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {expensesByCategory.slice(0, 8).map((cat) => (
              <div key={cat.value} className="bg-[#0a0a0b] rounded-xl p-4">
                <p className="text-gray-400 text-xs mb-1">{cat.taxLine}</p>
                <p className="text-white font-medium text-sm mb-1">{cat.label}</p>
                <p className="text-red-400 font-bold">{formatCurrency(cat.total)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search expenses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="px-3 py-2.5 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm"
            data-testid="year-filter"
          >
            <option value="all">All Years</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="px-3 py-2.5 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm"
            data-testid="month-filter"
          >
            {monthOptions.map((month) => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="col-span-2 md:col-span-1 px-3 py-2.5 bg-[#1a1a1f] border border-white/10 rounded-xl text-white text-sm"
          >
            <option value="all">All Categories</option>
            {expenseCategories.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Expenses List */}
      <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredExpenses.length === 0 ? (
          <div className="text-center py-20">
            <TrendingDown className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No expense records found for selected period</p>
          </div>
        ) : (
          <>
            {/* Mobile card view */}
            <div className="md:hidden divide-y divide-white/5">
              {filteredExpenses.map((item) => (
                <div key={item.id} className="p-4 hover:bg-white/5 transition-colors">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">{item.description}</p>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {formatDate(item.date)}{item.vendor ? ` · ${item.vendor}` : ''}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-red-400 font-semibold text-sm">-{formatCurrency(item.amount)}</p>
                      {item.is_tax_deductible && (
                        <p className="text-green-400 text-xs mt-0.5">Deductible</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400 truncate max-w-[60%]">
                      {expenseCategories.find(c => c.value === item.category)?.label || item.category}
                    </span>
                    <div className="flex items-center gap-1">
                      {item.evidence_file && (
                        <button
                          onClick={() => viewEvidence(item.evidence_file)}
                          className="p-1.5 text-blue-400 hover:text-blue-300 transition-colors"
                          data-testid={`view-evidence-${item.id}`}
                        >
                          <Paperclip className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedExpense(item);
                          setFormData({ ...item, date: item.date?.split('T')[0] || item.date });
                          if (item.evidence_file) {
                            const isPdf = item.evidence_file.toLowerCase().endsWith('.pdf');
                            setEvidencePreview(isPdf ? 'pdf' : `${API}/expenses/evidence/${item.evidence_file}`);
                          } else {
                            setEvidencePreview(null);
                          }
                          setShowModal(true);
                        }}
                        className="p-1.5 text-gray-400 hover:text-white transition-colors"
                        data-testid={`edit-expense-${item.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                        data-testid={`delete-expense-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Date</th>
                    <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Description</th>
                    <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Category</th>
                    <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">Vendor</th>
                    <th className="text-center text-gray-400 text-sm font-medium px-6 py-4">Evidence</th>
                    <th className="text-center text-gray-400 text-sm font-medium px-6 py-4">Deductible</th>
                    <th className="text-right text-gray-400 text-sm font-medium px-6 py-4">Amount</th>
                    <th className="text-right text-gray-400 text-sm font-medium px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((item) => (
                    <tr key={item.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 text-gray-400">{formatDate(item.date)}</td>
                      <td className="px-6 py-4 text-white">{item.description}</td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                          {expenseCategories.find(c => c.value === item.category)?.label || item.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-400">{item.vendor || '-'}</td>
                      <td className="px-6 py-4 text-center">
                        {item.evidence_file ? (
                          <button
                            onClick={() => viewEvidence(item.evidence_file)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                            data-testid={`view-evidence-${item.id}`}
                          >
                            <Paperclip className="w-3 h-3" />
                            <span className="text-xs">View</span>
                          </button>
                        ) : (
                          <span className="text-gray-500 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {item.is_tax_deductible ? (
                          <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right text-red-400 font-medium">-{formatCurrency(item.amount)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedExpense(item);
                              setFormData({
                                ...item,
                                date: item.date?.split('T')[0] || item.date,
                              });
                              if (item.evidence_file) {
                                const isPdf = item.evidence_file.toLowerCase().endsWith('.pdf');
                                setEvidencePreview(isPdf ? 'pdf' : `${API}/expenses/evidence/${item.evidence_file}`);
                              } else {
                                setEvidencePreview(null);
                              }
                              setShowModal(true);
                            }}
                            className="p-2 text-gray-400 hover:text-white transition-colors"
                            data-testid={`edit-expense-${item.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                            data-testid={`delete-expense-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative w-full max-w-lg bg-[#1a1a1f] rounded-2xl sm:rounded-3xl border border-white/10 p-4 sm:p-8 my-2 sm:my-0 max-h-[calc(100vh-1rem)] sm:max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-xl text-white font-medium mb-6">
              {selectedExpense ? 'Edit Expense' : 'Add Expense'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Amount *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Description *</label>
                <input
                  type="text"
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                  placeholder="e.g., Adobe Creative Cloud subscription"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">IRS Category (Schedule C) *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                >
                  {expenseCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label} ({cat.taxLine})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Vendor/Payee</label>
                  <input
                    type="text"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Payment Method</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                  >
                    {paymentMethods.map((pm) => (
                      <option key={pm.value} value={pm.value}>{pm.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Receipt/Reference #</label>
                <input
                  type="text"
                  value={formData.receipt_number}
                  onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white"
                />
              </div>

              {/* Evidence Upload Section */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Evidence (Receipt/Invoice)</label>
                {!formData.evidence_file ? (
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="evidence-upload"
                      data-testid="evidence-upload-input"
                    />
                    <label
                      htmlFor="evidence-upload"
                      className={`flex items-center justify-center gap-3 w-full py-4 bg-[#0a0a0b] border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-blue-500/50 transition-colors ${uploadingFile ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      {uploadingFile ? (
                        <>
                          <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                          <span className="text-gray-400">Uploading & compressing...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-400">Upload photo or PDF</span>
                        </>
                      )}
                    </label>
                    <p className="text-xs text-gray-500 mt-2">Images auto-compressed to ~500KB. Max: 5MB (images) / 10MB (PDF)</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-4 p-3 bg-[#0a0a0b] rounded-xl border border-white/10">
                    {evidencePreview === 'pdf' ? (
                      <div className="w-16 h-16 rounded-lg bg-red-500/20 flex items-center justify-center">
                        <FileText className="w-8 h-8 text-red-400" />
                      </div>
                    ) : (
                      <img
                        src={evidencePreview?.startsWith('blob:') ? evidencePreview : `${API}/expenses/evidence/${formData.evidence_file}`}
                        alt="Evidence"
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <p className="text-white text-sm truncate">{formData.evidence_file}</p>
                      <p className="text-gray-500 text-xs">Evidence attached</p>
                    </div>
                    <button
                      type="button"
                      onClick={removeEvidence}
                      className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                      data-testid="remove-evidence-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="tax_deductible"
                  checked={formData.is_tax_deductible}
                  onChange={(e) => setFormData({ ...formData, is_tax_deductible: e.target.checked })}
                  className="w-5 h-5 rounded bg-[#0a0a0b] border-white/10"
                />
                <label htmlFor="tax_deductible" className="text-gray-400 text-sm">
                  This expense is tax deductible
                </label>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white resize-none"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium transition-colors"
                  data-testid="save-expense-btn"
                >
                  {selectedExpense ? 'Update' : 'Add Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Evidence View Modal */}
      {showEvidenceModal && viewingEvidence && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => setShowEvidenceModal(false)} />
          <div className="relative max-w-4xl max-h-[90vh] overflow-auto">
            <button
              onClick={() => setShowEvidenceModal(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70"
              data-testid="close-evidence-modal"
            >
              <X className="w-5 h-5" />
            </button>
            {viewingEvidence.toLowerCase().endsWith('.pdf') ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <p className="text-gray-800 mb-4">PDF Document</p>
                <a
                  href={`${API}/expenses/evidence/${viewingEvidence}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                  data-testid="download-evidence-btn"
                >
                  <Eye className="w-5 h-5" />
                  Open PDF
                </a>
              </div>
            ) : (
              <img
                src={`${API}/expenses/evidence/${viewingEvidence}`}
                alt="Expense Evidence"
                className="max-w-full rounded-xl shadow-2xl"
              />
            )}
          </div>
        </div>
      )}

      {/* Expense Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowReportModal(false)} />
          <div className="relative w-full max-w-4xl bg-white rounded-2xl p-0 my-8 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <h2 className="text-xl text-gray-800 font-semibold">Expense Report - {getReportPeriodLabel()}</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                  data-testid="print-expense-report-btn"
                >
                  <Printer className="w-4 h-4" />
                  Print / Save PDF
                </button>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Printable Report Content */}
            <div ref={reportRef} className="p-8" style={{ backgroundColor: 'white' }}>
              {/* Report Header */}
              <div style={{ borderBottom: '2px solid #e5e7eb', paddingBottom: '20px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                      {companySettings?.business_name || 'Business Expense Report'}
                    </h1>
                    {companySettings?.address && (
                      <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0 0' }}>
                        {companySettings.address}
                        {companySettings.city && `, ${companySettings.city}`}
                        {companySettings.state && `, ${companySettings.state}`}
                        {companySettings.zip && ` ${companySettings.zip}`}
                      </p>
                    )}
                    {companySettings?.ein && (
                      <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0 0' }}>
                        EIN: {companySettings.ein}
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626', margin: 0 }}>
                      EXPENSE REPORT
                    </h2>
                    <p style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0 0' }}>
                      Period: {getReportPeriodLabel()}
                    </p>
                    <p style={{ color: '#6b7280', fontSize: '12px', margin: '4px 0 0 0' }}>
                      Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary Section */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px' }}>
                  <p style={{ color: '#991b1b', fontSize: '12px', fontWeight: '500', margin: 0 }}>TOTAL EXPENSES</p>
                  <p style={{ color: '#991b1b', fontSize: '24px', fontWeight: 'bold', margin: '4px 0 0 0' }}>{formatCurrency(totalExpenses)}</p>
                </div>
                <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '16px' }}>
                  <p style={{ color: '#166534', fontSize: '12px', fontWeight: '500', margin: 0 }}>TAX DEDUCTIBLE</p>
                  <p style={{ color: '#166534', fontSize: '24px', fontWeight: 'bold', margin: '4px 0 0 0' }}>{formatCurrency(taxDeductible)}</p>
                </div>
                <div style={{ backgroundColor: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '16px' }}>
                  <p style={{ color: '#5b21b6', fontSize: '12px', fontWeight: '500', margin: 0 }}>TRANSACTIONS</p>
                  <p style={{ color: '#5b21b6', fontSize: '24px', fontWeight: 'bold', margin: '4px 0 0 0' }}>{filteredExpenses.length}</p>
                </div>
              </div>

              {/* Expenses by IRS Category */}
              {expensesByCategory.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Expenses by IRS Schedule C Category
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                    {expensesByCategory.map((cat) => (
                      <div key={cat.value} style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
                        <p style={{ color: '#9ca3af', fontSize: '10px', margin: 0 }}>{cat.taxLine}</p>
                        <p style={{ color: '#6b7280', fontSize: '12px', margin: '2px 0 0 0' }}>{cat.label}</p>
                        <p style={{ color: '#dc2626', fontSize: '16px', fontWeight: '600', margin: '4px 0 0 0' }}>{formatCurrency(cat.total)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Transaction List */}
              <div>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Transaction Details
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Description</th>
                      <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Vendor</th>
                      <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Category (IRS Line)</th>
                      <th style={{ textAlign: 'center', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Deductible</th>
                      <th style={{ textAlign: 'right', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#374151', fontWeight: '600' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.map((item, index) => {
                      const category = expenseCategories.find(c => c.value === item.category);
                      return (
                        <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb' }}>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>{formatDate(item.date)}</td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', color: '#111827' }}>{item.description}</td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>{item.vendor || '-'}</td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                            {category?.label || item.category} <span style={{ color: '#9ca3af' }}>({category?.taxLine || '-'})</span>
                          </td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                            {item.is_tax_deductible ? '✓' : '-'}
                          </td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', color: '#dc2626', fontWeight: '600', textAlign: 'right' }}>
                            {formatCurrency(item.amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#fef2f2' }}>
                      <td colSpan="5" style={{ padding: '12px 10px', borderTop: '2px solid #e5e7eb', fontWeight: '600', color: '#991b1b', textAlign: 'right' }}>
                        TOTAL:
                      </td>
                      <td style={{ padding: '12px 10px', borderTop: '2px solid #e5e7eb', fontWeight: 'bold', color: '#991b1b', textAlign: 'right', fontSize: '14px' }}>
                        {formatCurrency(totalExpenses)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Footer */}
              <div style={{ marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
                <p style={{ color: '#9ca3af', fontSize: '11px', margin: 0 }}>
                  This expense report was generated for tax and accounting purposes. Please retain with supporting documentation.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
